import i18next from "../configure-i18n"
// Make luxon accessible from the browser's console for debugging purposes and
// add some purely informational logging
import type { ResourcesConfig } from "aws-amplify"
import { Amplify } from "aws-amplify"
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito"
import { Settings } from "luxon"
import type { PostHog } from "posthog-js"
import posthog from "posthog-js"
import { getClientEnv } from "./client-env-init"
import { fspConfig } from "./fsp-config.ts"
import { I18nSetup } from "./i18n-setup.ts"

export async function clientInit(): Promise<{ posthog: PostHog }> {
  const func = "clientInit"

  // 1) --------------------------------------------------
  console.log("📣 Parsing client environment variables", { func })
  const parsedEnv = getClientEnv()

  // 2) --------------------------------------------------
  console.log("🌐", { func }, "Initializing i18next")
  const _i18n = i18next.createInstance()
  const i18n = I18nSetup.useAllPlugins(_i18n)
  I18nSetup.attachEventListeners(i18n, Settings)

  const fallbackLng = I18nSetup.pickFallbackLng(window.location.hostname)
  const i18nextOptions = I18nSetup.createClientOptions(fallbackLng)

  console.log(
    "🌐",
    { func },
    `The i18next config sets the following namespaces: ${i18nextOptions.ns}.
  Each of them will cause a separate request to Locize, one for the current language and one for the fallback language.
  Note that the most common case is that the current language and the fallback language are the same, in which case there will be only one request per namespace.`,
  )
  const cb = () => console.log("🌐🎉", { func }, "Translations loaded!")
  // top-level await is supported in vite when it's configured for ESM and need to target modern browsers (es2022+)
  await i18n.init(i18nextOptions, cb)

  // we need to initialize the i18next as soon as we can!
  console.log("🌐 Initializing i18next")
  i18next.init()

  // 3) --------------------------------------------------
  console.log("📣", { func }, "Initializing Amplify's Auth settings")
  // We explicitly set localStorage even though it's Amplify's default to make this security choice
  // visible and protect against future version changes.
  cognitoUserPoolsTokenProvider.setKeyValueStorage({
    getItem: async (key) => localStorage.getItem(key),
    setItem: async (key, value) => localStorage.setItem(key, value),
    removeItem: async (key) => localStorage.removeItem(key),
    clear: async () => localStorage.clear(),
  })
  const authConfig: ResourcesConfig["Auth"] = {
    Cognito: {
      userPoolId: fspConfig.cognito.userPoolId,
      userPoolClientId: fspConfig.cognito.userPoolWebClientId,
      signUpVerificationMethod: "code",
      // These user attributes must align with the configuration defined in our Cognito User Pool.
      userAttributes: {
        name: {
          required: true,
        },
        email: {
          required: true,
        },
      },
    },
  }
  Amplify.configure({
    Auth: authConfig,
  })

  // 4) --------------------------------------------------
  // Same project as foodie-web. Autocapture is off, as there: events are sent on purpose.
  console.log("📣 Initializing PostHog analytics", { func })
  posthog.init(parsedEnv.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN, {
    api_host: parsedEnv.VITE_PUBLIC_POSTHOG_HOST,
    defaults: "2026-01-30",
    autocapture: false,
  })

  return { posthog }
}
